/**
 * Canvas Drawing Service
 * 
 * Programmatic drawing of architectural symbols on floor plans.
 * More reliable than AI for simple geometric shapes.
 */

import { 
  AmendmentType as SharedAmendmentType, 
  EditorSelectionBox, 
  DrawingResult 
} from '../types';

// Re-export types for backwards compatibility
export type { DrawingResult };

// Local selection box interface (matches EditorSelectionBox)
export interface SelectionBox {
  x: number;      // 0-1 normalized
  y: number;      // 0-1 normalized
  width: number;  // 0-1 normalized
  height: number; // 0-1 normalized
}

// Drawing settings
const LINE_COLOR = '#000000';
const LINE_WIDTH = 2;
const THICK_LINE = 4;

// Standardized ratios for consistent drawing
const DOOR_ARC_RATIO = 0.85;  // Arc radius as ratio of larger dimension

/**
 * Calculate door arc radius - standardized for all door functions
 */
function calculateDoorArc(w: number, h: number): number {
  return Math.max(w, h) * DOOR_ARC_RATIO;
}

/**
 * Sample the background color from the image at a given position
 * Uses a grid pattern with more sample points for better accuracy
 * Returns the average of the lightest colors (most likely background)
 */
function sampleBackgroundColor(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): string {
  const samples: number[][] = [];
  
  // Sample a 3x4 grid pattern for better coverage
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 4; j++) {
      const sx = x + (width * (i + 0.5)) / 3;
      const sy = y + (height * (j + 0.5)) / 4;
      
      try {
        const imageData = ctx.getImageData(Math.floor(sx), Math.floor(sy), 1, 1);
        const [r, g, b] = imageData.data;
        samples.push([r, g, b]);
      } catch {
        // Ignore sampling errors
      }
    }
  }

  if (samples.length === 0) {
    return '#FFFFFF'; // Fallback to white
  }

  // Sort by brightness (sum of RGB) - lightest first
  samples.sort((a, b) => (b[0] + b[1] + b[2]) - (a[0] + a[1] + a[2]));
  
  // Average the lightest 50% of samples
  const topHalf = samples.slice(0, Math.ceil(samples.length / 2));
  const avgR = Math.round(topHalf.reduce((s, p) => s + p[0], 0) / topHalf.length);
  const avgG = Math.round(topHalf.reduce((s, p) => s + p[1], 0) / topHalf.length);
  const avgB = Math.round(topHalf.reduce((s, p) => s + p[2], 0) / topHalf.length);

  return `rgb(${avgR}, ${avgG}, ${avgB})`;
}

/**
 * Load an image from URL and return as HTMLImageElement
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

/**
 * Create a canvas with the image drawn on it
 */
async function createCanvasFromImage(imageUrl: string): Promise<{
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
}> {
  const img = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.drawImage(img, 0, 0);
  return { canvas, ctx, width: canvas.width, height: canvas.height };
}

/**
 * Convert normalized selection to pixel coordinates
 */
function selectionToPixels(selection: SelectionBox, width: number, height: number) {
  return {
    x: Math.round(selection.x * width),
    y: Math.round(selection.y * height),
    w: Math.round(selection.width * width),
    h: Math.round(selection.height * height),
  };
}

// ============================================================================
// DRAWING FUNCTIONS
// ============================================================================

/**
 * Draw a left-swing door symbol (hinge on left, swings into room)
 * Arc goes from bottom-left corner curving right
 */
export async function drawDoorLeft(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    // Use standardized arc calculation
    const arcRadius = calculateDoorArc(w, h);
    
    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = THICK_LINE;
    ctx.lineCap = 'round';

    // Hinge at bottom-left, door swings right/up
    const hingeX = x;
    const hingeY = y + h;

    // Draw the door arc
    ctx.beginPath();
    ctx.arc(hingeX, hingeY, arcRadius, -Math.PI / 2, 0);
    ctx.stroke();

    // Draw the door panel line
    ctx.beginPath();
    ctx.moveTo(hingeX, hingeY);
    ctx.lineTo(hingeX + arcRadius, hingeY);
    ctx.stroke();

    return {
      success: true,
      imageDataUrl: canvas.toDataURL('image/png'),
    };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a right-swing door symbol (hinge on right, swings into room)
 * Arc goes from bottom-right corner curving left
 */
export async function drawDoorRight(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    // Use standardized arc calculation
    const arcRadius = calculateDoorArc(w, h);
    
    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = THICK_LINE;
    ctx.lineCap = 'round';

    // Hinge at bottom-right, door swings left/up
    const hingeX = x + w;
    const hingeY = y + h;

    // Draw the door arc (mirrored)
    ctx.beginPath();
    ctx.arc(hingeX, hingeY, arcRadius, Math.PI, -Math.PI / 2);
    ctx.stroke();

    // Draw the door panel line
    ctx.beginPath();
    ctx.moveTo(hingeX, hingeY);
    ctx.lineTo(hingeX - arcRadius, hingeY);
    ctx.stroke();

    return {
      success: true,
      imageDataUrl: canvas.toDataURL('image/png'),
    };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a window symbol (double parallel lines)
 */
export async function drawWindow(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    // Sample background color before clearing
    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    
    // Clear the selection area with matching background
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';

    const isVertical = h > w;
    const padding = Math.min(w, h) * 0.15;
    const glassGap = Math.min(w, h) * 0.25;

    if (isVertical) {
      // Vertical window - draw horizontal parallel lines
      const centerY = y + h / 2;
      
      // Outer frame lines
      ctx.beginPath();
      ctx.moveTo(x + padding, y + padding);
      ctx.lineTo(x + padding, y + h - padding);
      ctx.moveTo(x + w - padding, y + padding);
      ctx.lineTo(x + w - padding, y + h - padding);
      ctx.stroke();

      // Glass indication (center lines)
      ctx.beginPath();
      ctx.moveTo(x + padding + glassGap, y + padding);
      ctx.lineTo(x + padding + glassGap, y + h - padding);
      ctx.moveTo(x + w - padding - glassGap, y + padding);
      ctx.lineTo(x + w - padding - glassGap, y + h - padding);
      ctx.stroke();
    } else {
      // Horizontal window
      const centerX = x + w / 2;
      
      // Outer frame lines
      ctx.beginPath();
      ctx.moveTo(x + padding, y + padding);
      ctx.lineTo(x + w - padding, y + padding);
      ctx.moveTo(x + padding, y + h - padding);
      ctx.lineTo(x + w - padding, y + h - padding);
      ctx.stroke();

      // Glass indication (center lines)
      ctx.beginPath();
      ctx.moveTo(x + padding, y + padding + glassGap);
      ctx.lineTo(x + w - padding, y + padding + glassGap);
      ctx.moveTo(x + padding, y + h - padding - glassGap);
      ctx.lineTo(x + w - padding, y + h - padding - glassGap);
      ctx.stroke();
    }

    return {
      success: true,
      imageDataUrl: canvas.toDataURL('image/png'),
    };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Remove content (clear to background color)
 */
export async function clearArea(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    // Sample background color before clearing
    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    
    // Fill with matching background
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    return {
      success: true,
      imageDataUrl: canvas.toDataURL('image/png'),
    };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a solid wall - fills the entire selection as a black rectangle
 * The selection width/height determines the wall thickness
 */
export async function drawWall(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    // Fill the entire selection with solid black (wall)
    ctx.fillStyle = LINE_COLOR;
    ctx.fillRect(x, y, w, h);

    return {
      success: true,
      imageDataUrl: canvas.toDataURL('image/png'),
    };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Flip door swing direction (mirror the arc)
 */
export async function flipDoorSwing(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    // Sample background color before clearing
    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    
    // Clear the selection area with matching background
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    // Use standardized arc calculation for consistency
    const arcRadius = calculateDoorArc(w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = THICK_LINE; // Use thick line for consistency with other doors
    ctx.lineCap = 'round';

    // Draw flipped door (opposite corner from normal doors)
    // Normal doors have hinge at bottom, flipped has hinge at top
    ctx.beginPath();
    const isVertical = h > w;
    if (isVertical) {
      // Door frame at top
      ctx.moveTo(x, y);
      ctx.lineTo(x + w, y);
      ctx.stroke();

      // Arc from top-left going down
      ctx.beginPath();
      ctx.arc(x, y, arcRadius, 0, Math.PI / 2);
      ctx.stroke();

      // Door panel
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + arcRadius);
      ctx.stroke();
    } else {
      // Door frame at left
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + h);
      ctx.stroke();

      // Arc from left going right
      ctx.beginPath();
      ctx.arc(x, y + h, arcRadius, -Math.PI / 2, 0);
      ctx.stroke();

      // Door panel
      ctx.beginPath();
      ctx.moveTo(x, y + h);
      ctx.lineTo(x + arcRadius, y + h);
      ctx.stroke();
    }

    return {
      success: true,
      imageDataUrl: canvas.toDataURL('image/png'),
    };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

// ============================================================================
// BATHROOM SYMBOLS
// ============================================================================

/**
 * Draw a toilet symbol (top-down view)
 */
export async function drawToilet(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;

    const centerX = x + w / 2;
    const centerY = y + h / 2;
    const bowlWidth = Math.min(w, h) * 0.7;
    const bowlHeight = Math.min(w, h) * 0.9;
    const tankWidth = bowlWidth * 0.8;
    const tankHeight = bowlHeight * 0.25;

    // Draw tank (rectangle at back)
    ctx.beginPath();
    ctx.rect(centerX - tankWidth / 2, y + h * 0.05, tankWidth, tankHeight);
    ctx.stroke();

    // Draw bowl (oval)
    ctx.beginPath();
    ctx.ellipse(centerX, centerY + h * 0.1, bowlWidth / 2, bowlHeight / 2.5, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Draw seat opening (smaller oval inside)
    ctx.beginPath();
    ctx.ellipse(centerX, centerY + h * 0.1, bowlWidth / 3, bowlHeight / 4, 0, 0, Math.PI * 2);
    ctx.stroke();

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a sink symbol (top-down view)
 */
export async function drawSink(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;

    const centerX = x + w / 2;
    const centerY = y + h / 2;
    const sinkW = w * 0.8;
    const sinkH = h * 0.7;

    // Draw outer sink (rounded rectangle)
    ctx.beginPath();
    ctx.roundRect(centerX - sinkW / 2, centerY - sinkH / 2, sinkW, sinkH, 8);
    ctx.stroke();

    // Draw inner basin (smaller rounded rectangle)
    const basinW = sinkW * 0.7;
    const basinH = sinkH * 0.6;
    ctx.beginPath();
    ctx.roundRect(centerX - basinW / 2, centerY - basinH / 2 + 5, basinW, basinH, 6);
    ctx.stroke();

    // Draw drain (small circle)
    ctx.beginPath();
    ctx.arc(centerX, centerY + 5, 4, 0, Math.PI * 2);
    ctx.fill();

    // Draw faucet (small rectangle at top)
    ctx.beginPath();
    ctx.rect(centerX - 8, y + h * 0.1, 16, 8);
    ctx.stroke();

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a bathtub symbol (top-down view)
 */
export async function drawBathtub(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;

    const padding = Math.min(w, h) * 0.1;

    // Draw outer tub (rounded rectangle)
    ctx.beginPath();
    ctx.roundRect(x + padding, y + padding, w - padding * 2, h - padding * 2, 15);
    ctx.stroke();

    // Draw inner tub (smaller rounded rectangle)
    const innerPad = padding * 2;
    ctx.beginPath();
    ctx.roundRect(x + innerPad, y + innerPad, w - innerPad * 2, h - innerPad * 2, 10);
    ctx.stroke();

    // Draw drain (small circle at one end)
    ctx.fillStyle = LINE_COLOR;
    ctx.beginPath();
    ctx.arc(x + w - padding * 3, y + h / 2, 5, 0, Math.PI * 2);
    ctx.fill();

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a shower cubicle symbol (top-down view with drain and door indication)
 */
export async function drawShower(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;

    const padding = Math.min(w, h) * 0.08;

    // Draw outer frame (shower enclosure)
    ctx.beginPath();
    ctx.rect(x + padding, y + padding, w - padding * 2, h - padding * 2);
    ctx.stroke();

    // Draw shower tray (inner rectangle with rounded corners)
    const innerPad = padding * 2.5;
    ctx.beginPath();
    ctx.roundRect(x + innerPad, y + innerPad, w - innerPad * 2, h - innerPad * 2, 6);
    ctx.stroke();

    // Draw drain (circle in center)
    const centerX = x + w / 2;
    const centerY = y + h / 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 6, 0, Math.PI * 2);
    ctx.stroke();
    // Drain grate (cross pattern)
    ctx.beginPath();
    ctx.moveTo(centerX - 4, centerY);
    ctx.lineTo(centerX + 4, centerY);
    ctx.moveTo(centerX, centerY - 4);
    ctx.lineTo(centerX, centerY + 4);
    ctx.stroke();

    // Draw shower head indicator (small rectangle at top)
    ctx.beginPath();
    ctx.rect(centerX - 10, y + padding + 2, 20, 6);
    ctx.stroke();

    // Draw door swing indication (dashed arc at bottom)
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    const doorRadius = Math.min(w, h) * 0.4;
    ctx.arc(x + padding, y + h - padding, doorRadius, -Math.PI / 2, 0);
    ctx.stroke();
    ctx.setLineDash([]); // Reset dash

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

// ============================================================================
// KITCHEN SYMBOLS
// ============================================================================

/**
 * Draw a stove/cooktop symbol (top-down view)
 */
export async function drawStove(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;

    const padding = Math.min(w, h) * 0.1;

    // Draw outer frame
    ctx.beginPath();
    ctx.rect(x + padding, y + padding, w - padding * 2, h - padding * 2);
    ctx.stroke();

    // Draw 4 burners (circles in 2x2 grid)
    const burnerRadius = Math.min(w, h) * 0.15;
    const gapX = (w - padding * 2) / 3;
    const gapY = (h - padding * 2) / 3;

    const burnerPositions = [
      { bx: x + padding + gapX, by: y + padding + gapY },
      { bx: x + padding + gapX * 2, by: y + padding + gapY },
      { bx: x + padding + gapX, by: y + padding + gapY * 2 },
      { bx: x + padding + gapX * 2, by: y + padding + gapY * 2 },
    ];

    for (const pos of burnerPositions) {
      // Outer ring
      ctx.beginPath();
      ctx.arc(pos.bx, pos.by, burnerRadius, 0, Math.PI * 2);
      ctx.stroke();
      // Inner ring
      ctx.beginPath();
      ctx.arc(pos.bx, pos.by, burnerRadius * 0.5, 0, Math.PI * 2);
      ctx.stroke();
    }

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

/**
 * Draw a kitchen sink symbol (double basin)
 */
export async function drawKitchenSink(
  imageUrl: string,
  selection: SelectionBox
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;

    const padding = Math.min(w, h) * 0.1;
    const isWide = w > h;

    // Draw outer frame
    ctx.beginPath();
    ctx.roundRect(x + padding, y + padding, w - padding * 2, h - padding * 2, 5);
    ctx.stroke();

    // Draw two basins
    if (isWide) {
      const basinW = (w - padding * 3) / 2;
      const basinH = h - padding * 4;
      // Left basin
      ctx.beginPath();
      ctx.roundRect(x + padding * 1.5, y + padding * 2, basinW, basinH, 4);
      ctx.stroke();
      // Right basin
      ctx.beginPath();
      ctx.roundRect(x + padding * 1.5 + basinW + padding, y + padding * 2, basinW, basinH, 4);
      ctx.stroke();
      // Drains
      ctx.fillStyle = LINE_COLOR;
      ctx.beginPath();
      ctx.arc(x + padding * 1.5 + basinW / 2, y + h / 2, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x + padding * 2.5 + basinW * 1.5, y + h / 2, 3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const basinW = w - padding * 4;
      const basinH = (h - padding * 3) / 2;
      // Top basin
      ctx.beginPath();
      ctx.roundRect(x + padding * 2, y + padding * 1.5, basinW, basinH, 4);
      ctx.stroke();
      // Bottom basin
      ctx.beginPath();
      ctx.roundRect(x + padding * 2, y + padding * 1.5 + basinH + padding, basinW, basinH, 4);
      ctx.stroke();
    }

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

// ============================================================================
// TEXT LABELS
// ============================================================================

/**
 * Draw text label at the selection position
 * Supports multi-line text (split by \n)
 */
export async function drawText(
  imageUrl: string,
  selection: SelectionBox,
  text: string,
  fontSize: number = 24
): Promise<DrawingResult> {
  try {
    const { canvas, ctx, width, height } = await createCanvasFromImage(imageUrl);
    const { x, y, w, h } = selectionToPixels(selection, width, height);

    // Sample background color and clear the area
    const bgColor = sampleBackgroundColor(ctx, x, y, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    // Split text into lines
    const lines = text.split('\n');
    
    // Setup font
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = LINE_COLOR;

    // Calculate line height and total text block height
    const lineHeight = fontSize * 1.2;
    const totalTextHeight = lines.length * lineHeight;
    
    // Calculate starting Y position to center the text block
    const centerX = x + w / 2;
    const startY = y + (h - totalTextHeight) / 2 + lineHeight / 2;

    // Draw each line
    lines.forEach((line, index) => {
      const lineY = startY + (index * lineHeight);
      ctx.fillText(line, centerX, lineY);
    });

    return { success: true, imageDataUrl: canvas.toDataURL('image/png') };
  } catch (error: any) {
    return { success: false, imageDataUrl: '', error: error.message };
  }
}

// ============================================================================
// MAIN HANDLER
// ============================================================================

// Re-export AmendmentType from shared types for backwards compatibility
export type AmendmentType = SharedAmendmentType;

/**
 * Execute a canvas drawing amendment
 * @param text - Optional text for addText action
 * @param fontSize - Optional font size for addText action
 */
export async function executeCanvasAmendment(
  imageUrl: string,
  selection: SelectionBox,
  actionType: AmendmentType,
  text?: string,
  fontSize?: number
): Promise<DrawingResult> {
  console.log('='.repeat(60));
  console.log('CANVAS DRAWING');
  console.log('Action:', actionType);
  console.log('Selection:', selection);
  if (text) console.log('Text:', text);
  if (fontSize) console.log('Font Size:', fontSize);
  console.log('='.repeat(60));

  switch (actionType) {
    case 'addDoorLeft':
      return drawDoorLeft(imageUrl, selection);
    case 'addDoorRight':
      return drawDoorRight(imageUrl, selection);
    case 'addWindow':
      return drawWindow(imageUrl, selection);
    case 'removeWall':
    case 'clearObject':
      return clearArea(imageUrl, selection);
    case 'buildWall':
      return drawWall(imageUrl, selection);
    case 'flipSwing':
      return flipDoorSwing(imageUrl, selection);
    case 'addToilet':
      return drawToilet(imageUrl, selection);
    case 'addSink':
      return drawSink(imageUrl, selection);
    case 'addBathtub':
      return drawBathtub(imageUrl, selection);
    case 'addShower':
      return drawShower(imageUrl, selection);
    case 'addStove':
      return drawStove(imageUrl, selection);
    case 'addKitchenSink':
      return drawKitchenSink(imageUrl, selection);
    case 'addText':
      if (!text) return { success: false, imageDataUrl: '', error: 'No text provided' };
      return drawText(imageUrl, selection, text, fontSize);
    default:
      return { success: false, imageDataUrl: '', error: 'Unknown action type' };
  }
}
